/**
 * Artikelstamm-Bereinigung -- Familie, Modell, SKU.
 *
 * WOFUER SIE DA IST
 *
 * Der Bestand ist ueber Jahre gewachsen. Ihn Artikel fuer Artikel zu
 * pruefen hiesse, 357 Akten zu oeffnen und sich zu merken, welche schon
 * dran waren. Diese Ansicht arbeitet stattdessen die Ordnung ab, in der
 * der Bestand gebaut ist: eine Familie, darin ein Modell, darin seine
 * SKUs -- und die Artikel derselben Familie, die noch keinem Modell
 * zugeordnet sind.
 *
 * DIE VIER ENTSCHEIDUNGEN, DIE SIE PRAEGEN
 *
 *   1. UEBERSICHT, DANN DETAIL. Die Familienliste nennt Zahlen, nicht
 *      Artikel. Wer alles auf einmal zeigt, zeigt nichts.
 *
 *   2. ES WIRD HIER NICHT BESTAETIGT. Kein Merkmalswert wird von dieser
 *      Ansicht auf `belastbar` gesetzt. Wer einen Wert entscheiden will,
 *      geht in die Akte -- dort steht das vorhandene Formular mit seiner
 *      Rechtepruefung. Ein zweites Eingabefeld waere ein zweiter Ort, an
 *      dem eine Regel fehlen kann.
 *
 *   3. NICHTS WIRD VORGESCHLAGEN. Die nicht zugeordneten Artikel stehen
 *      da, weil sie derselben Familie angehoeren -- nicht, weil HOMEBASE
 *      glaubt, sie gehoerten zu diesem Modell. Aehnliche Artikelnummern
 *      sind kein Beleg, und eine Ansicht, die sie wie einen macht, fuehrt
 *      genau zu den Zusammenfuehrungen, die dieser Bestand nicht braucht.
 *
 *   4. DIE ANSICHT NENNT IHRE EIGENE GRENZE. HOMEBASE speichert keine
 *      DTE-Bezeichnung und keinen DTE-Preis. Statt dafuer leere Spalten
 *      anzubieten -- die aussehen wuerden wie fehlende Daten statt wie
 *      fehlende Anbindung -- steht unter der Tabelle, was vergleichbar
 *      ist und was nicht.
 *
 * WAS SIE SCHREIBT
 *
 * Genau eines: einen Pruefhinweis. Faellt bei der Arbeit auf, dass DTE
 * veraltet ist, wird das hier festgehalten -- mit Notiz, sonst ist es
 * nicht abarbeitbar. HOMEBASE schreibt dabei NICHT nach DTE. Der Hinweis
 * ist eine Notiz an einer Stelle, an der sie wiedergefunden wird.
 */
import { useCallback, useEffect, useState } from "react";
import type {
  CleanupArticleRow, CleanupFamilyProgress, CleanupModelView, ProductModelListItem,
} from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { familyLabel } from "../i18n/terminology.ts";
import { EmptyState, ErrorState, LoadingState } from "./States.tsx";
import { formatNumber } from "./ProductProperties.tsx";
import { verfuegbarkeitswort } from "../lib/verfuegbarkeit.ts";
import "./Cleanup.css";

/*
 * Die Feldnamen der Vergleichsliste -- ausgeschrieben, nicht aus dem Code
 * zusammengesetzt. Ein Schluessel, den `t(\`...${x}\`)` erst zur Laufzeit
 * bildet, ist fuer die Uebersetzungspruefung unsichtbar: er faellt als
 * unbenutzt auf und fehlt trotzdem, wenn jemand ihn loescht.
 */
function feldwort(feld: string, t: I18n["t"]): string {
  switch (feld) {
    case "merkmale":    return t("cleanup.field.merkmale");
    case "bestand":     return t("cleanup.field.bestand");
    case "bezeichnung": return t("cleanup.field.bezeichnung");
    case "preis":       return t("cleanup.field.preis");
    case "lieferant":   return t("cleanup.field.lieferant");
    default:            return feld;
  }
}

/** Der Hinweistyp, den diese Ansicht oeffnen kann. Genau einer. */
const DTE_FLAG = "dte_korrekturbedarf";

export function Cleanup({ mayWrite, onOpenArticle }: {
  mayWrite: boolean;
  onOpenArticle: (articleId: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;

  const [families, setFamilies] = useState<readonly CleanupFamilyProgress[] | null>(null);
  const [familyId, setFamilyId] = useState<number | null>(null);
  const [models, setModels] = useState<readonly ProductModelListItem[]>([]);
  const [modelId, setModelId] = useState<number | null>(null);
  const [view, setView] = useState<CleanupModelView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let lebt = true;
    setLoading(true);
    api.cleanupFamilies()
      .then((r) => { if (lebt) { setFamilies(r.items); setError(null); } })
      .catch((e) => { if (lebt) setError(errorText(e, i18n)); })
      .finally(() => { if (lebt) setLoading(false); });
    return () => { lebt = false; };
  }, [i18n]);

  /* Die Modelle der gewaehlten Familie -- aus der bestehenden Modelliste,
     nicht aus einem zweiten Endpunkt. */
  useEffect(() => {
    if (familyId === null) { setModels([]); setModelId(null); return; }
    let lebt = true;
    api.models({ familyId })
      .then((r) => { if (lebt) setModels(r.items); })
      .catch(() => { if (lebt) setModels([]); });
    return () => { lebt = false; };
  }, [familyId]);

  const ladeModell = useCallback((id: number) => {
    setModelId(id);
    setLoading(true);
    api.cleanupModel(id)
      .then((v) => { setView(v); setError(null); })
      .catch((e) => setError(errorText(e, i18n)))
      .finally(() => setLoading(false));
  }, [i18n]);

  const neuLaden = useCallback(() => {
    if (modelId !== null) ladeModell(modelId);
  }, [modelId, ladeModell]);

  if (error !== null) return <ErrorState message={error} />;
  if (loading && families === null) return <LoadingState />;

  return (
    <div className="cleanup">
      <h2>{t("cleanup.title")}</h2>
      <p className="cleanup__lead">{t("cleanup.lead")}</p>

      <FamilyProgress
        items={families ?? []} i18n={i18n}
        selected={familyId}
        onSelect={(id) => { setFamilyId(id); setModelId(null); setView(null); }} />

      {familyId !== null && (
        <div className="cleanup__models">
          <h3>{t("cleanup.chooseModel")}</h3>
          {models.length === 0
            ? <EmptyState title={t("cleanup.noModels")} />
            : <div className="cleanup__modelbuttons">
                {models.map((m) => (
                  <button key={m.id} type="button"
                    className={`btn ${modelId === m.id ? "btn--primary" : ""}`}
                    onClick={() => ladeModell(m.id)}>{m.code}</button>
                ))}
              </div>}
        </div>
      )}

      {view !== null && (
        <ModelCleanup view={view} i18n={i18n} mayWrite={mayWrite}
          onOpenArticle={onOpenArticle} onChanged={neuLaden} />
      )}
    </div>
  );
}

/**
 * Der Familienfortschritt.
 *
 * Bewusst Zahlen und kein Balken: „80 % erledigt" laedt dazu ein, den
 * Rest fuer klein zu halten. Zwei offene Konflikte in einer Familie mit
 * vier Artikeln sind nicht klein.
 */
function FamilyProgress({ items, i18n, selected, onSelect }: {
  items: readonly CleanupFamilyProgress[];
  i18n: I18n;
  selected: number | null;
  onSelect: (id: number) => void;
}): React.JSX.Element {
  const t = i18n.t;
  if (items.length === 0) return <EmptyState title={t("cleanup.noFamilies")} />;
  return (
    <div className="cleanup__tablewrap">
      <table className="cleanup__table cleanup__table--families">
        <thead>
          <tr>
            <th>{t("cleanup.colFamily")}</th>
            <th className="cleanup__num">{t("cleanup.colModels")}</th>
            <th className="cleanup__num">{t("cleanup.colArticles")}</th>
            <th className="cleanup__num">{t("cleanup.colAssigned")}</th>
            <th className="cleanup__num">{t("cleanup.colUnassigned")}</th>
            <th className="cleanup__num">{t("cleanup.colClassified")}</th>
            <th className="cleanup__num">{t("cleanup.colConflicts")}</th>
            <th className="cleanup__num">{t("cleanup.colUnconfirmed")}</th>
            <th className="cleanup__num">{t("cleanup.colFlags")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((f) => (
            <tr key={f.familyId}
                className={selected === f.familyId ? "cleanup__row--selected" : ""}>
              <td>
                <button type="button" className="cleanup__link"
                  onClick={() => onSelect(f.familyId)}>
                  {familyLabel(i18n, f.familyCode, f.familyName)}
                </button>
              </td>
              <td className="cleanup__num">{f.models}</td>
              <td className="cleanup__num">{f.articles}</td>
              <td className="cleanup__num">{f.assigned}</td>
              {/*
                * OHNE MODELL -- und daneben, wie viele davon bewusst keine
                * Leuchte sind. Ein Ersatzteil ohne Produktmodell ist kein
                * Pflegerueckstand. Die beiden Zahlen stehen nebeneinander
                * statt als Differenz: wer nur die Differenz sieht, weiss
                * nicht, ob Artikel geklaert oder verschwunden sind.
                */}
              <td className="cleanup__num">
                {f.unassigned - f.unassignedClassified > 0
                  ? <span className="cleanup__open">{f.unassigned}</span>
                  : f.unassigned}
              </td>
              <td className="cleanup__num">
                {f.unassignedClassified > 0
                  ? <span title={t("cleanup.classifiedHint")}>{f.unassignedClassified}</span>
                  : 0}
              </td>
              {/*
                * Konflikte, und in Klammern die davon bereits bestaetigten.
                * Die zweite Zahl ist die interessantere: dort hat jemand
                * entschieden, waehrend der Widerspruch offen stand.
                */}
              <td className="cleanup__num">
                {f.conflicts > 0
                  ? <span className="cleanup__conflict"
                      title={t("cleanup.conflictsConfirmedHint", { n: f.conflictsConfirmed })}>
                      {f.conflicts}
                      {f.conflictsConfirmed > 0 && ` (${f.conflictsConfirmed})`}
                    </span>
                  : 0}
              </td>
              <td className="cleanup__num">{f.unconfirmedProperties}</td>
              <td className="cleanup__num">{f.openFlags}</td>
            </tr>
          ))}
        </tbody>
        {/*
          * ── DIE SUMMENZEILE (§17) ───────────────────────────────────
          *
          * SIE BEANTWORTET DIE FRAGE, DIE MAN VOR DER ERSTEN FAMILIE
          * HAT: wie gross ist das ueberhaupt? Ohne sie liest man neun
          * Zeilen und addiert im Kopf.
          *
          * GERECHNET AUS DEN ZEILEN DIESER TABELLE und nicht aus einer
          * zweiten Abfrage. Eine Summe, die der Server eigens liefert,
          * kann von der Tabelle abweichen -- und dann glaubt jeder der
          * Zahl, die naeher an seiner Erwartung liegt.
          *
          * KEINE PROZENTE UND KEIN BALKEN -- aus demselben Grund, aus
          * dem FamilyProgress keinen hat: „80 % erledigt" laedt dazu
          * ein, den Rest fuer klein zu halten.
          */}
        <tfoot>
          <tr className="cleanup__sumzeile">
            <th scope="row" title={t("cleanup.sumHint")}>{t("cleanup.sumRow")}</th>
            {([
              (f: CleanupFamilyProgress) => f.models,
              (f: CleanupFamilyProgress) => f.articles,
              (f: CleanupFamilyProgress) => f.assigned,
              (f: CleanupFamilyProgress) => f.unassigned,
              (f: CleanupFamilyProgress) => f.unassignedClassified,
              (f: CleanupFamilyProgress) => f.conflicts,
              (f: CleanupFamilyProgress) => f.unconfirmedProperties,
              (f: CleanupFamilyProgress) => f.openFlags,
            ] as const).map((lies, i) => (
              <td className="cleanup__num" key={i}>
                {items.reduce((n, f) => n + lies(f), 0)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ModelCleanup({ view, i18n, mayWrite, onOpenArticle, onChanged }: {
  view: CleanupModelView;
  i18n: I18n;
  mayWrite: boolean;
  onOpenArticle: (articleId: number) => void;
  onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="cleanup__model">
      <h3>{view.modelCode} — {view.modelName}</h3>

      <h4>{t("cleanup.skus", { n: view.skus.length })}</h4>
      {view.skus.length === 0
        ? <EmptyState title={t("cleanup.noSkus")} />
        : <ArticleTable rows={view.skus} i18n={i18n} mayWrite={mayWrite}
            onOpenArticle={onOpenArticle} onChanged={onChanged} />}

      {/*
        * DIE NICHT ZUGEORDNETEN. Ohne sie waere die Bereinigung blind fuer
        * genau die Artikel, um die es geht: die, die noch nirgends
        * haengen. Der Hinweistext sagt ausdruecklich, dass hier nichts
        * vorgeschlagen wird.
        */}
      <h4>{t("cleanup.unassigned", { n: view.unassignedInFamily.length })}</h4>
      <p className="cleanup__note">{t("cleanup.unassignedHint")}</p>
      {view.unassignedInFamily.length === 0
        ? <EmptyState title={t("cleanup.noUnassigned")} />
        : <ArticleTable rows={view.unassignedInFamily} i18n={i18n} mayWrite={mayWrite}
            onOpenArticle={onOpenArticle} onChanged={onChanged} />}

      {/*
        * DIE GRENZE DER ANSICHT, AUSGESPROCHEN.
        *
        * Sie steht unter der Tabelle und nicht im Kleingedruckten: wer
        * die Spalten liest, muss wissen, dass es zu Bezeichnung und
        * Preis gar keinen DTE-Wert zum Vergleichen gibt -- sonst haelt
        * er das Fehlen fuer einen Pflegemangel.
        */}
      <div className="cleanup__limits">
        <h4>{t("cleanup.dteScope")}</h4>
        <ul>
          {view.dteVergleich.map((d) => (
            <li key={d.feld} className={d.vergleichbar
              ? "cleanup__cmp--yes" : "cleanup__cmp--no"}>
              {feldwort(d.feld, t)}
              {": "}
              {d.vergleichbar ? t("cleanup.comparable") : t("cleanup.notComparable")}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ArticleTable({ rows, i18n, mayWrite, onOpenArticle, onChanged }: {
  rows: readonly CleanupArticleRow[];
  i18n: I18n;
  mayWrite: boolean;
  onOpenArticle: (articleId: number) => void;
  onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="cleanup__tablewrap">
      <table className="cleanup__table cleanup__table--articles">
        <thead>
          <tr>
            <th>{t("cleanup.colArticle")}</th>
            <th>{t("cleanup.colDescription")}</th>
            <th className="cleanup__num">{t("cleanup.colProps")}</th>
            <th>{t("cleanup.colConflict")}</th>
            <th className="cleanup__num">{t("cleanup.colPrice")}</th>
            <th>{t("cleanup.colStock")}</th>
            <th>{t("cleanup.colSupplier")}</th>
            <th>{t("cleanup.colOpen")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <ArticleRow key={r.articleId} row={r} i18n={i18n} mayWrite={mayWrite}
              onOpenArticle={onOpenArticle} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArticleRow({ row, i18n, mayWrite, onOpenArticle, onChanged }: {
  row: CleanupArticleRow;
  i18n: I18n;
  mayWrite: boolean;
  onOpenArticle: (articleId: number) => void;
  onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [formOffen, setFormOffen] = useState(false);
  const bereitsOffen = row.openFlags.some((f) => f.code === DTE_FLAG);

  return (
    <>
      <tr className="cleanup__articlerow">
        <td>
          <button type="button" className="cleanup__link"
            onClick={() => onOpenArticle(row.articleId)}>
            <code>{row.articleNumber}</code>
          </button>
          {row.focusRank !== null && (
            <span className="cleanup__focus">{t("cleanup.focus", { n: row.focusRank })}</span>
          )}
          {/* Die Rolle sagt, ob ein fehlendes Produktmodell ueberhaupt
              eine Frage ist. Sie steht deshalb an der Nummer, nicht in
              einer eigenen Spalte weit rechts. */}
          <span className="cleanup__roles">
            {row.roles.length > 0
              ? row.roles.map((r) => i18n.tCode("articleRole", r, r)).join(", ")
              : t("cleanup.roleNone")}
          </span>
        </td>
        <td className="cleanup__desc">{row.shortDescription ?? "—"}</td>
        {/*
          * DREI ZAHLEN, NICHT EINE QUOTE. Bestaetigt / Warnung / offen --
          * in dieser Reihenfolge, und die dritte ist die Arbeit.
          */}
        <td className="cleanup__num">
          <span title={t("cleanup.propsHint")}>
            {row.properties.belastbar} / {row.properties.warnung} /{" "}
            <strong>{row.properties.pruefungErforderlich}</strong>
          </span>
        </td>
        <td>
          {row.conflicts.length === 0
            ? <span className="cleanup__none">—</span>
            : <ul className="cleanup__conflicts">
                {row.conflicts.map((c) => (
                  <li key={c.propertyId}
                      className={c.quality === "belastbar" ? "cleanup__conflict--confirmed" : ""}>
                    <span className="cleanup__cdef">{c.definition}</span>{" "}
                    <span className="cleanup__cdetail">{c.detail ?? c.findingCode}</span>
                    {/*
                      * Der Vermerk, der die eigentliche Arbeit markiert:
                      * hier steht ein bestaetigter Wert gegen einen
                      * offenen Widerspruch. Kein Fehler -- eine
                      * Entscheidung, die jemand getroffen hat und die
                      * nachvollziehbar bleiben muss.
                      */}
                    {c.quality === "belastbar" && (
                      <span className="cleanup__confirmedmark"
                        title={t("cleanup.confirmedDespiteHint")}>
                        {t("cleanup.confirmedDespite")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>}
        </td>
        <td className="cleanup__num cleanup__price">
          {row.listPriceEur !== null
            ? <span title={row.listPriceValidFrom
                ? t("cleanup.priceValidFrom", { date: row.listPriceValidFrom })
                : undefined}>{formatNumber(row.listPriceEur, i18n.lang)} €</span>
            : <span className="cleanup__none">—</span>}
        </td>
        {/*
          * DER BESTAND IMMER MIT STICHTAG. Eine Menge ohne Datum ist
          * keine Auskunft -- sie sieht aus wie heute und ist es meistens
          * nicht.
          */}
        <td className="cleanup__stock">
          <span className={`cleanup__avail cleanup__avail--${row.availability}`}>
            {verfuegbarkeitswort(row.availability, t)}
          </span>
          {row.stock !== null && (
            <span className="cleanup__stockdetail">
              {" "}{formatNumber(row.stock.quantity, i18n.lang)} {row.stock.unit}
              {" · "}
              <span className="cleanup__asof"
                title={t("cleanup.asOfHint", { source: row.stock.source })}>
                {t("cleanup.asOf", { date: row.stock.asOf })}
              </span>
            </span>
          )}
        </td>
        <td className="cleanup__supplier">
          {row.supplier !== null
            ? <span title={row.supplier.supplierArticleNumber ?? undefined}>
                {row.supplier.name}
              </span>
            : <span className="cleanup__none">—</span>}
        </td>
        <td>
          {row.openFlags.length === 0 && row.openProposals === 0
            && row.openInformationRequests === 0
            ? <span className="cleanup__none">—</span>
            : <ul className="cleanup__open-list">
                {row.openFlags.map((f) => (
                  <li key={f.id}>
                    <span className="cleanup__flag">{f.name}</span>
                    {f.note !== null && <span className="cleanup__flagnote"> {f.note}</span>}
                    {mayWrite && (
                      <button type="button" className="btn btn--small"
                        onClick={() => {
                          void api.resolveReviewFlag(f.id, { resolutionNote: null })
                            .then(onChanged);
                        }}>{t("cleanup.resolve")}</button>
                    )}
                  </li>
                ))}
                {row.openProposals > 0 && (
                  <li>{t("cleanup.proposals", { n: row.openProposals })}</li>
                )}
                {row.openInformationRequests > 0 && (
                  <li>{t("cleanup.infoRequests", { n: row.openInformationRequests })}</li>
                )}
              </ul>}
          {mayWrite && !bereitsOffen && !formOffen && (
            <button type="button" className="btn btn--small cleanup__addflag"
              onClick={() => setFormOffen(true)}>{t("cleanup.markDte")}</button>
          )}
        </td>
      </tr>
      {formOffen && (
        <tr className="cleanup__formrow">
          <td colSpan={8}>
            <DteFlagForm articleId={row.articleId} i18n={i18n}
              onDone={() => { setFormOffen(false); onChanged(); }}
              onCancel={() => setFormOffen(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Der DTE-Korrekturbedarf.
 *
 * Die Notiz ist Pflicht -- der Server weist einen leeren Hinweis ab, und
 * die Schaltflaeche bleibt bis dahin gesperrt. Ein Hinweis, der nicht
 * sagt, WAS in DTE falsch ist, bleibt fuer immer offen und macht die
 * Liste unbrauchbar.
 *
 * Der Satz darunter ist keine Beruhigung, sondern eine Tatsache, die man
 * beim Klicken wissen muss: hier passiert nichts in DTE.
 */
function DteFlagForm({ articleId, i18n, onDone, onCancel }: {
  articleId: number;
  i18n: I18n;
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  return (
    <div className="cleanup__form">
      <label htmlFor={`dte-note-${articleId}`}>{t("cleanup.dteNoteLabel")}</label>
      <input id={`dte-note-${articleId}`} type="text" value={note} maxLength={1000}
        placeholder={t("cleanup.dteNotePlaceholder")}
        onChange={(e) => setNote(e.target.value)} />
      <button type="button" className="btn btn--primary"
        disabled={busy || note.trim() === ""}
        onClick={() => {
          setBusy(true); setFehler(null);
          api.openReviewFlag(articleId, { code: DTE_FLAG, note: note.trim() })
            .then(onDone)
            .catch((e) => setFehler(errorText(e, i18n)))
            .finally(() => setBusy(false));
        }}>{t("cleanup.dteSave")}</button>
      <button type="button" className="btn" onClick={onCancel}>{t("cleanup.cancel")}</button>
      <p className="cleanup__nowrite">{t("cleanup.noDteWrite")}</p>
      {fehler !== null && <p className="cleanup__error">{fehler}</p>}
    </div>
  );
}
